/**
 * Comprehensive Jobs Management
 * Full implementation of all Jobs API endpoints
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { Badge } from '../UI/Badge';
import { 
  listJobs,
  getJob,
  cancelJob,
  requeueJob,
  clearJobs,
  submitIngestJob,
  submitParseJob,
  submitIndexJob,
} from '../../api/jobs';
import { listArtifacts } from '../../api/artifacts';
import { 
  getSystemMonitoring, 
  getGPUSummary,
  restartWorker,
  type SystemMonitoringResponse 
} from '../../api/systemMonitoring';
import { useWorkspace } from '../../hooks/useWorkspace';
import { IngestArtifactsModal } from './IngestArtifactsModal';
import { JobExpandedView } from './JobExpandedView';
import { 
  Zap, 
  Search, 
  Filter, 
  ChevronLeft, 
  ChevronRight, 
  ChevronDown,
  RefreshCw,
  XCircle,
  Trash2,
  AlertCircle,
  CheckCircle,
  Clock,
  Loader,
  FileText,
  Database,
  Search as SearchIcon,
  Server,
  Cpu,
  Activity,
  HardDrive,
} from 'lucide-react';

export function JobList() {
  const { projectId } = useWorkspace();
  const queryClient = useQueryClient();
  
  // Read state from URL (q / job_id prefill search — e.g. notification deep links)
  const params = new URLSearchParams(window.location.search);
  const [searchTerm, setSearchTerm] = useState(
    () => params.get('q') || params.get('job_id') || ''
  );
  const [filterType, setFilterType] = useState<string>(params.get('type') || 'all');
  const [filterStatus, setFilterStatus] = useState<string>(params.get('status') || 'all');
  const [sortBy, setSortBy] = useState<'created_at' | 'type' | 'status'>((params.get('sort') as any) || 'created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>((params.get('order') as any) || 'desc');
  const [page, setPage] = useState(parseInt(params.get('page') || '0'));
  const [pageSize] = useState(20);
  const [showIngestModal, setShowIngestModal] = useState(false);
  const [expandedJobs, setExpandedJobs] = useState<Set<string>>(new Set());
  const [showSystemMonitoring, setShowSystemMonitoring] = useState(false);
  
  // Update URL when filters change (preserve e.g. tab=jobs from /data?tab=jobs)
  const updateURL = () => {
    const url = new URL(window.location.href);
    if (filterType !== 'all') url.searchParams.set('type', filterType);
    else url.searchParams.delete('type');
    if (filterStatus !== 'all') url.searchParams.set('status', filterStatus);
    else url.searchParams.delete('status');
    url.searchParams.set('sort', sortBy);
    url.searchParams.set('order', sortOrder);
    if (page > 0) url.searchParams.set('page', String(page));
    else url.searchParams.delete('page');
    const q = searchTerm.trim();
    if (q) url.searchParams.set('q', q);
    else url.searchParams.delete('q');
    url.searchParams.delete('job_id'); // prefer canonical `q` once synced
    window.history.pushState({}, '', url.toString());
  };
  
  React.useEffect(() => {
    updateURL();
  }, [filterType, filterStatus, sortBy, sortOrder, page, searchTerm]);

  const { data: jobs, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ['jobs', projectId],
    queryFn: () => listJobs(projectId!),
    enabled: !!projectId,
    refetchInterval: 5000,
  });

  // Also fetch artifacts list to use in expanded view
  const { data: artifactsResponse } = useQuery({
    queryKey: ['artifacts', projectId],
    queryFn: () => listArtifacts(projectId!),
    enabled: !!projectId,
  });
  // Normalize: listArtifacts returns { artifacts: Artifact[], ... }; ensure we always have an array
  const artifactsList = Array.isArray(artifactsResponse)
    ? artifactsResponse
    : (artifactsResponse?.artifacts ?? []);

  // System monitoring query
  const { data: systemMonitoring, isLoading: isMonitoringLoading, refetch: refetchMonitoring } = useQuery({
    queryKey: ['systemMonitoring'],
    queryFn: getSystemMonitoring,
    enabled: showSystemMonitoring,
    refetchInterval: showSystemMonitoring ? 10000 : false, // Refresh every 10s when visible
    retry: 1,
  });

  const cancelMutation = useMutation({
    mutationFn: (jobId: string) => cancelJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs', projectId] });
    },
  });

  const requeueMutation = useMutation({
    mutationFn: (jobId: string) => requeueJob(jobId, true), // Force reprocess already-processed files
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs', projectId] });
    },
  });

  const clearMutation = useMutation({
    mutationFn: (status?: string) => clearJobs(projectId!, status),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['jobs', projectId] });
      alert(`Cleared ${result.deleted} job${result.deleted !== 1 ? 's' : ''}`);
    },
  });

  const filteredJobs = React.useMemo(() => {
    if (!jobs) return [];
    
    return jobs.filter((job) => {
      const matchesSearch = 
        job.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.status?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesType = filterType === 'all' || job.type === filterType;
      const matchesStatus = filterStatus === 'all' || job.status === filterStatus;
      
      return matchesSearch && matchesType && matchesStatus;
    }).sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  }, [jobs, searchTerm, filterType, filterStatus, sortBy, sortOrder]);

  const paginatedJobs = React.useMemo(() => {
    const start = page * pageSize;
    return filteredJobs.slice(start, start + pageSize);
  }, [filteredJobs, page, pageSize]);

  const totalPages = Math.ceil(filteredJobs.length / pageSize);

  const runningJobs = jobs?.filter(j => ['pending', 'queued', 'running'].includes(j.status)).length || 0;
  const failedJobs = jobs?.filter(j => j.status === 'failed').length || 0;
  const completedJobs = jobs?.filter(j => j.status === 'completed').length || 0;
  const bm25Jobs = jobs?.filter(j => j.type === 'build_bm25_index').length || 0;
  const pendingBm25Jobs = jobs?.filter(j => j.type === 'build_bm25_index' && ['pending', 'queued', 'running'].includes(j.status)).length || 0;

  const statusVariant = {
    pending: 'warning',
    queued: 'warning',
    running: 'info',
    completed: 'success',
    failed: 'error',
    cancelled: 'default',
  } as const;

  const statusIcons = {
    pending: Clock,
    queued: Clock,
    running: Loader,
    completed: CheckCircle,
    failed: XCircle,
    cancelled: XCircle,
  };

  const typeIcons = {
    ingest: Database,
    parse: FileText,
    index: SearchIcon,
    report_generation: FileText,
    build_bm25_index: SearchIcon,  // BM25 keyword index build
    opportunity_ranking: Zap,
  };

  // Job type labels for better display
  const typeLabels: Record<string, string> = {
    ingest: 'Ingest',
    parse: 'Parse',
    index: 'Index',
    report_generation: 'Report Generation',
    build_bm25_index: 'BM25 Keyword Index',
    opportunity_ranking: 'Opportunity Ranking',
  };

  const toggleJobExpanded = (jobId: string) => {
    const newExpanded = new Set(expandedJobs);
    if (newExpanded.has(jobId)) {
      newExpanded.delete(jobId);
    } else {
      newExpanded.add(jobId);
    }
    setExpandedJobs(newExpanded);
  };

  if (!projectId) {
    return (
      <Card className="dark:bg-slate-800 dark:border-slate-700">
        <p className="text-center py-12 text-theme-text-secondary">Select a project to view jobs</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Zap className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-theme-text">Jobs</h1>
            <p className="text-theme-text-secondary mt-1">
              {filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''}
              {runningJobs > 0 && (
                <span className="ml-2 text-blue-600 font-medium">
                  ({runningJobs} running)
                </span>
              )}
              {failedJobs > 0 && (
                <span className="ml-2 text-red-600 font-medium">
                  ({failedJobs} failed)
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            size="sm"
            onClick={() => setShowIngestModal(true)}
          >
            <Database className="h-4 w-4 mr-2" />
            Ingest Artifacts
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => {
              if (window.confirm(`Clear ${runningJobs} queued/stuck jobs?`)) {
                clearMutation.mutate('queued');
              }
            }}
            disabled={clearMutation.isPending || runningJobs === 0}
            className="text-orange-600 hover:text-orange-700"
          >
            <XCircle className="h-4 w-4 mr-2" />
            Clear Queued ({runningJobs})
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              if (window.confirm(`Clear ${failedJobs} failed job${failedJobs !== 1 ? 's' : ''}?`)) {
                clearMutation.mutate('failed');
              }
            }}
            disabled={clearMutation.isPending || failedJobs === 0}
            className="text-red-600 hover:text-red-700"
          >
            <XCircle className="h-4 w-4 mr-2" />
            Clear Failed ({failedJobs})
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => {
              if (window.confirm(`Clear ALL ${jobs?.length || 0} jobs?`)) {
                clearMutation.mutate(undefined as any);
              }
            }}
            disabled={clearMutation.isPending || !jobs || jobs.length === 0}
            className="text-red-600 hover:text-red-700 border-red-200"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Clear All
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => refetch()}
            disabled={isRefetching}
            aria-busy={isRefetching}
            title={isRefetching ? 'Refreshing jobs…' : 'Reload jobs from server'}
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 shrink-0 ${isRefetching ? 'animate-spin text-primary-600' : ''}`}
              aria-hidden
            />
            {isRefetching ? 'Refreshing…' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* System Monitoring Panel */}
      <Card className="border-2 border-purple-200">
        <div 
          className="flex items-center justify-between cursor-pointer p-4"
          onClick={() => {
            setShowSystemMonitoring(!showSystemMonitoring);
            if (!showSystemMonitoring) {
              refetchMonitoring();
            }
          }}
        >
          <div className="flex items-center gap-3">
            <Server className="h-6 w-6 text-purple-600" />
            <div>
              <h3 className="text-lg font-semibold text-theme-text">
                GPU & Worker Infrastructure
              </h3>
              <p className="text-sm text-theme-text-secondary">
                Monitor 7 GPUs across 2 servers, LiteLLM proxy, and local workers
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {systemMonitoring && systemMonitoring.alerts.length > 0 && (
              <Badge variant="warning" className="animate-pulse">
                {systemMonitoring.alerts.length} Alert{systemMonitoring.alerts.length !== 1 ? 's' : ''}
              </Badge>
            )}
            <ChevronDown 
              className={`h-5 w-5 text-theme-text-secondary transition-transform ${showSystemMonitoring ? 'rotate-180' : ''}`}
            />
          </div>
        </div>

        {showSystemMonitoring && (
          <div className="border-t border-theme-border p-4 space-y-4">
            {isMonitoringLoading ? (
              <div className="text-center py-8">
                <Loader className="h-8 w-8 animate-spin mx-auto text-purple-600" />
                <p className="text-sm text-theme-text-secondary mt-2">Loading system status...</p>
              </div>
            ) : systemMonitoring ? (
              <>
                {/* Alerts */}
                {systemMonitoring.alerts.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-red-900 mb-2">System Alerts</h4>
                        <ul className="space-y-1 text-sm text-red-800">
                          {systemMonitoring.alerts.map((alert, idx) => (
                            <li key={idx}>{alert}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* GPU Infrastructure */}
                <div>
                  <h4 className="font-semibold text-theme-text mb-3 flex items-center gap-2">
                    <Server className="h-5 w-5 text-purple-600" />
                    GPU Infrastructure
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(systemMonitoring.gpu_infrastructure).map(([serverName, instances]) => (
                      <div key={serverName} className="bg-theme-elevated rounded-lg p-3">
                        <h5 className="font-medium text-theme-text mb-2 capitalize">
                          {serverName} Server
                        </h5>
                        <div className="space-y-2">
                          {instances.map((instance) => (
                            <div key={`${instance.host}:${instance.port}`} className="flex flex-col gap-0.5 text-sm">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <div className={`h-2 w-2 rounded-full ${instance.status === 'healthy' ? 'bg-green-500' : 'bg-red-500'}`} />
                                  <span className="font-medium text-theme-text">{instance.name}</span>
                                </div>
                                <div className="text-xs text-theme-text-secondary">
                                  {instance.gpu_count}× {instance.gpu_model}
                                  {instance.speed_tokens_per_sec != null && ` • ${instance.speed_tokens_per_sec} tok/s`}
                                </div>
                              </div>
                              {instance.status === 'unhealthy' && (instance.unhealthy_reason || instance.error_type) && (
                                <div className="text-xs text-red-600 pl-4">
                                  {instance.error_type && <span className="font-medium">{instance.error_type}</span>}
                                  {instance.unhealthy_reason && ` — ${instance.unhealthy_reason}`}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 text-xs text-theme-text-secondary">
                    Total: {Object.values(systemMonitoring.gpu_infrastructure).reduce((sum, instances) => 
                      sum + instances.reduce((s, i) => s + i.gpu_count, 0), 0
                    )} GPUs
                  </div>
                </div>

                {/* LiteLLM Proxy Status */}
                <div>
                  <h4 className="font-semibold text-theme-text mb-3 flex items-center gap-2">
                    <Activity className="h-5 w-5 text-blue-600" />
                    LiteLLM Proxy
                  </h4>
                  <div className="bg-theme-elevated rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`h-3 w-3 rounded-full ${
                          systemMonitoring.litellm_proxy.status === 'healthy' ? 'bg-green-500' :
                          systemMonitoring.litellm_proxy.status === 'degraded' ? 'bg-yellow-500' : 'bg-red-500'
                        }`} />
                        <span className="font-medium text-theme-text capitalize">
                          {systemMonitoring.litellm_proxy.status}
                        </span>
                      </div>
                      <div className="text-sm text-theme-text-secondary">
                        {systemMonitoring.litellm_proxy.healthy_backends}/{systemMonitoring.litellm_proxy.healthy_backends + systemMonitoring.litellm_proxy.unhealthy_backends} backends healthy
                        {systemMonitoring.litellm_proxy.response_time_ms != null && ` • ${systemMonitoring.litellm_proxy.response_time_ms}ms`}
                      </div>
                    </div>
                    {systemMonitoring.litellm_proxy.status === 'down' && (systemMonitoring.litellm_proxy.error_type || systemMonitoring.litellm_proxy.error_message) && (
                      <div className="mt-2 text-xs text-red-600">
                        {systemMonitoring.litellm_proxy.error_type && <span className="font-medium">{systemMonitoring.litellm_proxy.error_type}</span>}
                        {systemMonitoring.litellm_proxy.error_message && ` — ${systemMonitoring.litellm_proxy.error_message}`}
                        {systemMonitoring.litellm_proxy.error_type === 'timeout' && ' (may be transient)'}
                        {systemMonitoring.litellm_proxy.error_type === 'connection_refused' && ' (proxy likely not running)'}
                      </div>
                    )}
                  </div>
                </div>

                {/* Client-Side Load Balancer */}
                {systemMonitoring.load_balancer && (
                  <div>
                    <h4 className="font-semibold text-theme-text mb-3 flex items-center gap-2">
                      <Activity className="h-5 w-5 text-purple-600" />
                      Client-Side Load Balancer
                      <span className="text-xs font-normal text-theme-text-secondary">(Direct Ollama Fallback)</span>
                    </h4>
                    <div className="bg-theme-elevated rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-theme-text-secondary">
                          {systemMonitoring.load_balancer.total_backends} backends, {systemMonitoring.load_balancer.total_capacity} concurrent slots
                        </span>
                        <span className="text-xs text-theme-text-secondary">
                          {systemMonitoring.load_balancer.backends.reduce((sum, b) => sum + b.active_requests, 0)} active requests
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {systemMonitoring.load_balancer.backends.map((backend) => (
                          <div key={`${backend.host}:${backend.port}`} className="text-xs bg-theme-paper rounded p-2">
                            <div className="font-medium text-theme-text">{backend.name}</div>
                            <div className="text-theme-text-secondary">{backend.host}:{backend.port}</div>
                            <div className="text-theme-text-secondary mt-1">
                              {backend.active_requests}/{backend.num_parallel} slots ({backend.utilization_percent}%)
                            </div>
                            <div className="w-full bg-theme-border rounded-full h-1.5 mt-1">
                              <div 
                                className={`h-1.5 rounded-full ${backend.utilization_percent > 75 ? 'bg-red-500' : backend.utilization_percent > 50 ? 'bg-yellow-500' : 'bg-green-500'}`}
                                style={{ width: `${backend.utilization_percent}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Celery Workers */}
                <div>
                  <h4 className="font-semibold text-theme-text mb-3 flex items-center gap-2">
                    <Cpu className="h-5 w-5 text-green-600" />
                    Celery Workers
                  </h4>
                  {systemMonitoring.celery_workers.length === 0 ? (
                    <div className="bg-red-50 rounded-lg p-3 text-sm text-red-800">
                      No active Celery workers detected
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {systemMonitoring.celery_workers.map((worker) => (
                        <div key={worker.worker_name} className="bg-theme-elevated rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium text-theme-text text-sm">
                                {worker.worker_name}
                              </div>
                              <div className="text-xs text-theme-text-secondary mt-1">
                                {worker.active_tasks} active • {worker.processed_tasks} processed • {worker.failed_tasks} failed
                              </div>
                            </div>
                            <Badge variant={worker.status === 'active' ? 'success' : 'default'}>
                              {worker.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Local Resources */}
                <div>
                  <h4 className="font-semibold text-theme-text mb-3 flex items-center gap-2">
                    <HardDrive className="h-5 w-5 text-orange-600" />
                    Local Resources
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-theme-elevated rounded-lg p-3">
                      <div className="text-xs text-theme-text-secondary">CPU</div>
                      <div className="text-lg font-bold text-theme-text">
                        {systemMonitoring.local_resources.cpu_percent.toFixed(1)}%
                      </div>
                    </div>
                    <div className="bg-theme-elevated rounded-lg p-3">
                      <div className="text-xs text-theme-text-secondary">Memory</div>
                      <div className="text-lg font-bold text-theme-text">
                        {systemMonitoring.local_resources.memory_percent.toFixed(1)}%
                      </div>
                    </div>
                    <div className="bg-theme-elevated rounded-lg p-3">
                      <div className="text-xs text-theme-text-secondary">Celery Workers</div>
                      <div className="text-lg font-bold text-theme-text">
                        {systemMonitoring.local_resources.celery_workers}
                      </div>
                    </div>
                    <div className="bg-theme-elevated rounded-lg p-3">
                      <div className="text-xs text-theme-text-secondary">API Servers</div>
                      <div className="text-lg font-bold text-theme-text">
                        {systemMonitoring.local_resources.api_servers}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recommendations */}
                {systemMonitoring.recommendations.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-blue-900 mb-2">Recommendations</h4>
                        <ul className="space-y-1 text-sm text-blue-800 list-disc list-inside">
                          {systemMonitoring.recommendations.map((rec, idx) => (
                            <li key={idx}>{rec}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Refresh timestamp */}
                <div className="text-xs text-theme-text-secondary text-center">
                  Last updated: {new Date(systemMonitoring.timestamp).toLocaleTimeString()}
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-theme-text-secondary">
                Failed to load system monitoring data
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <div className="flex items-center gap-3">
            <Loader className="h-8 w-8 text-blue-600" />
            <div>
              <div className="text-2xl font-bold text-blue-900">{runningJobs}</div>
              <div className="text-sm text-blue-700">Running</div>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <div>
              <div className="text-2xl font-bold text-green-900">{completedJobs}</div>
              <div className="text-sm text-green-700">Completed</div>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-pink-50 border-red-200">
          <div className="flex items-center gap-3">
            <XCircle className="h-8 w-8 text-red-600" />
            <div>
              <div className="text-2xl font-bold text-red-900">{failedJobs}</div>
              <div className="text-sm text-red-700">Failed</div>
            </div>
          </div>
        </Card>

        {/* BM25 Index Jobs Card */}
        <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
          <div className="flex items-center gap-3">
            <SearchIcon className="h-8 w-8 text-indigo-600" />
            <div>
              <div className="text-2xl font-bold text-indigo-900">{bm25Jobs}</div>
              <div className="text-sm text-indigo-700">
                BM25 Index
                {pendingBm25Jobs > 0 && (
                  <span className="ml-1 text-amber-600">({pendingBm25Jobs} queued)</span>
                )}
              </div>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-gray-50 to-slate-50 border-gray-200">
          <div className="flex items-center gap-3">
            <Zap className="h-8 w-8 text-theme-text-secondary dark:text-gray-400" />
            <div>
              <div className="text-2xl font-bold text-theme-text dark:text-black">{jobs?.length || 0}</div>
              <div className="text-sm text-theme-text-secondary dark:text-black">Total</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <div className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-theme-text-secondary z-10 dark:text-black" />
              <Input
                placeholder="Search jobs by type, ID, or status..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                className="pl-10 dark:text-black"
              />
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-theme-text mb-1">
                <Filter className="inline h-4 w-4 mr-1" />
                Filter by Type
              </label>
              <select
                value={filterType}
                onChange={(e) => {
                  setFilterType(e.target.value);
                  setPage(0);
                }}
                className="w-full px-3 py-2 border border-theme-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none bg-theme-paper text-theme-text dark:text-black"
              >
                <option value="all">All Types</option>
                {Array.from(new Set(jobs?.map(j => j.type).filter(Boolean))).map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-theme-text mb-1">
                Filter by Status
              </label>
              <select
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setPage(0);
                }}
                className="w-full px-3 py-2 border border-theme-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none bg-theme-paper text-theme-text dark:text-black"
              >
                <option value="all">All Status</option>
                <option value="running">Running</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="queued">Queued</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-theme-text mb-1">
                Sort By
              </label>
              <div className="flex gap-2">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="flex-1 px-3 py-2 border border-theme-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none bg-theme-paper text-theme-text dark:text-black"
                >
                  <option value="created_at">Created Date</option>
                  <option value="type">Type</option>
                  <option value="status">Status</option>
                </select>
                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="px-3 py-2 border border-theme-border rounded-lg hover:bg-theme-surface-hover text-theme-text dark:text-black"
                  title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Jobs List */}
      <div className="space-y-3 w-full">
        {paginatedJobs.map((job) => {
          const StatusIcon = statusIcons[job.status as keyof typeof statusIcons] || Clock;
          const TypeIcon = typeIcons[job.type as keyof typeof typeIcons] || Zap;
          const isRunning = ['pending', 'queued', 'running'].includes(job.status);
          const isExpanded = expandedJobs.has(job.id);
          const artifactIds = job.input_data?.artifact_ids || [];
          const outputData = job.output_data || {};

          return (
            <Card key={job.id} className="hover:shadow-lg transition-shadow w-full min-w-0 max-w-full overflow-hidden">
              <div className="flex flex-col gap-4 min-w-0 w-full">
                <div className="flex flex-col gap-3 min-w-0 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                  <div className="flex gap-3 min-w-0 flex-1">
                    <TypeIcon className="h-6 w-6 text-blue-600 shrink-0 mt-0.5" aria-hidden />
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-semibold text-theme-text capitalize text-base [overflow-wrap:anywhere] leading-snug">
                          {(job as any).name || typeLabels[job.type] || job.type}
                        </h3>
                        <Badge variant={statusVariant[job.status as keyof typeof statusVariant]} className="shrink-0">
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {job.status}
                        </Badge>
                        {artifactIds.length > 0 && (
                          <Badge variant="default" className="text-xs shrink-0">
                            {artifactIds.length} artifact{artifactIds.length !== 1 ? 's' : ''}
                          </Badge>
                        )}
                        {(job as any).actual_item_count !== undefined && (job as any).actual_item_count !== null && (
                          <Badge variant="success" className="text-xs shrink-0">
                            {(job as any).actual_item_count.toLocaleString()} items synced
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        {(job as any).source_name && (
                          <span className="text-sm text-blue-600 font-medium break-words">
                            {(job as any).source_name}
                          </span>
                        )}
                        <p className="text-sm text-theme-text-secondary font-mono break-all min-w-0 max-w-full">{job.id}</p>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-xs shrink-0"
                          onClick={() => {
                            navigator.clipboard.writeText(job.id);
                          }}
                          title="Copy Job ID"
                        >
                          <FileText className="h-3 w-3" />
                        </Button>
                      </div>
                      {(job as any).filters_applied && Object.keys((job as any).filters_applied).length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          <span className="text-xs text-theme-text-secondary">Filters:</span>
                          {Object.entries((job as any).filters_applied).map(([key, value]) => (
                            <Badge key={key} variant="default" className="text-xs max-w-full break-words">
                              {key}: {Array.isArray(value) ? value.join(', ') : String(value)}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 shrink-0 w-full sm:w-auto sm:justify-end">
                    {artifactIds.length > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleJobExpanded(job.id)}
                        className="text-theme-text-secondary hover:text-theme-text shrink-0"
                      >
                        <ChevronDown className={`h-4 w-4 mr-2 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        {isExpanded ? 'Collapse' : 'Expand'}
                      </Button>
                    )}
                    {isRunning && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => cancelMutation.mutate(job.id)}
                        disabled={cancelMutation.isPending}
                        className="text-red-600 hover:text-red-700 shrink-0"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Cancel
                      </Button>
                    )}
                    {(job.status === 'cancelled' || job.status === 'failed' || job.status === 'completed') && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => requeueMutation.mutate(job.id)}
                        disabled={requeueMutation.isPending}
                        className="text-blue-600 hover:text-blue-700 shrink-0"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Requeue
                      </Button>
                    )}
                  </div>
                </div>

                <div className="min-w-0 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                    <div className="min-w-0">
                      <div className="text-theme-text-secondary text-xs mb-1">Created</div>
                      <div className="font-medium text-theme-text break-words">
                        {new Date(job.created_at).toLocaleString()}
                      </div>
                    </div>
                    {job.started_at && (
                      <div className="min-w-0">
                        <div className="text-theme-text-secondary text-xs mb-1">Started</div>
                        <div className="font-medium text-theme-text break-words">
                          {new Date(job.started_at).toLocaleString()}
                        </div>
                      </div>
                    )}
                    {job.completed_at && (
                      <div className="min-w-0 sm:col-span-2 lg:col-span-1">
                        <div className="text-theme-text-secondary text-xs mb-1">Completed</div>
                        <div className="font-medium text-theme-text break-words">
                          {new Date(job.completed_at).toLocaleString()}
                        </div>
                        {job.started_at && (
                          <div className="text-xs text-theme-text-secondary mt-1">
                            {(() => {
                              const durationMs = new Date(job.completed_at).getTime() - new Date(job.started_at).getTime();
                              const durationSec = Math.round(durationMs / 1000);
                              if (durationSec === 0 && durationMs > 0) {
                                return `Duration: <1s`;
                              } else if (durationSec === 0) {
                                return `Duration: 0s (check timestamps)`;
                              }
                              return `Duration: ${durationSec}s`;
                            })()}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {outputData.num_chunks_created !== undefined && !isExpanded && (
                      <div className="bg-green-50 rounded-lg p-2 min-w-0">
                        <div className="text-theme-text-secondary text-xs mb-1">Chunks Created</div>
                        <div className="font-semibold text-theme-text text-lg tabular-nums">
                          {outputData.num_chunks_created.toLocaleString()}
                        </div>
                        {(() => {
                          const jobArtifactIds = job.input_data?.artifact_ids || [];
                          const jobArtifacts = artifactsList.filter(a => jobArtifactIds.includes(a.id));
                          if (jobArtifacts.length > 0 && outputData.num_chunks_created === 0 && job.status === 'completed') {
                            return (
                              <div className="text-xs text-red-600 mt-1 flex items-center gap-1">
                                <AlertCircle className="h-3 w-3 shrink-0" />
                                Expected chunks but got 0
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    )}
                    {outputData.total_entities_created !== undefined && !isExpanded && (
                      <div className="bg-orange-50 rounded-lg p-2 min-w-0">
                        <div className="text-theme-text-secondary text-xs mb-1">Entities Created</div>
                        <div className="font-semibold text-theme-text text-lg tabular-nums">
                          {outputData.total_entities_created.toLocaleString()}
                        </div>
                        {(() => {
                          const jobArtifactIds = job.input_data?.artifact_ids || [];
                          const jobArtifacts = artifactsList.filter(a => jobArtifactIds.includes(a.id));
                          if (jobArtifacts.length > 0 && outputData.total_entities_created === 0 && job.status === 'completed') {
                            return (
                              <div className="text-xs text-red-600 mt-1 flex items-center gap-1">
                                <AlertCircle className="h-3 w-3 shrink-0" />
                                Expected entities but got 0
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    )}
                    {job.type === 'build_bm25_index' && outputData.entry_count !== undefined && (
                      <div className="bg-indigo-50 rounded-lg p-2 min-w-0">
                        <div className="text-theme-text-secondary text-xs mb-1">Entries Indexed</div>
                        <div className="font-semibold text-theme-text text-lg tabular-nums">
                          {outputData.entry_count.toLocaleString()}
                        </div>
                        {outputData.bm25_indexed && (
                          <div className="text-xs text-green-600 mt-1 flex items-center gap-1">
                            <CheckCircle className="h-3 w-3 shrink-0" />
                            BM25 Index Ready
                          </div>
                        )}
                      </div>
                    )}
                    {job.type === 'build_bm25_index' && outputData.elapsed_seconds !== undefined && (
                      <div className="bg-slate-50 rounded-lg p-2 min-w-0">
                        <div className="text-theme-text-secondary text-xs mb-1">Build Time</div>
                        <div className="font-semibold text-theme-text text-lg tabular-nums">
                          {outputData.elapsed_seconds}s
                        </div>
                      </div>
                    )}
                  </div>

                  {job.error_message && (
                    <div className="mt-1 bg-red-50 border border-red-200 rounded-lg p-3 min-w-0">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-red-900">Error</div>
                          <div className="text-sm text-red-700 break-words">{job.error_message}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Enhanced Progress Display with Queue Position & Stage */}
                  {(job.progress_data || job.queue_position || job.current_stage) && (
                    <div className="space-y-2 min-w-0">
                      {job.queue_position && ['pending', 'queued'].includes(job.status) && (
                        <div className="flex flex-wrap items-center gap-2 text-sm bg-amber-50 rounded-lg px-3 py-2">
                          <Clock className="h-4 w-4 text-amber-600 shrink-0" />
                          <span className="text-amber-800 break-words">
                            Queue Position: <span className="font-bold">#{job.queue_position}</span>
                          </span>
                          {job.queue_position === 1 && (
                            <Badge variant="warning" className="text-xs shrink-0">Next up</Badge>
                          )}
                        </div>
                      )}

                      {(job.current_stage || job.stage_message) && (
                        <div className="flex flex-col gap-2 text-sm bg-blue-50 rounded-lg px-3 py-2 sm:flex-row sm:items-start">
                          <div className="flex gap-2 min-w-0 flex-1">
                            {isRunning ? (
                              <Loader className="h-4 w-4 text-blue-600 animate-spin shrink-0 mt-0.5" />
                            ) : (
                              <Zap className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
                            )}
                            <div className="min-w-0 flex-1">
                              <span className="text-blue-800 font-medium capitalize">
                                {job.current_stage || 'Processing'}
                              </span>
                              {job.stage_message && (
                                <span className="text-blue-600 block sm:inline sm:ml-2 break-words">
                                  — {job.stage_message}
                                </span>
                              )}
                            </div>
                          </div>
                          {job.estimated_time_remaining && job.estimated_time_remaining > 0 && (
                            <span className="text-xs text-blue-600 shrink-0 sm:ml-auto">
                              ~{Math.ceil(job.estimated_time_remaining / 60)}min remaining
                            </span>
                          )}
                        </div>
                      )}

                      {(job.progress_percentage !== null && job.progress_percentage !== undefined) || (job.progress_data && isRunning) ? (
                        <div className="min-w-0 w-full">
                          <div className="flex items-center justify-between gap-2 text-sm mb-1">
                            <span className="text-theme-text-secondary shrink-0">Progress</span>
                            <span className="font-medium text-theme-text tabular-nums">
                              {job.progress_percentage !== null && job.progress_percentage !== undefined
                                ? `${Math.round(job.progress_percentage)}%`
                                : `${Math.round((job.progress_data?.current || 0) / (job.progress_data?.total || 1) * 100)}%`}
                            </span>
                          </div>
                          <div className="w-full min-w-0 bg-theme-border rounded-full h-2.5 overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${
                                job.status === 'completed' ? 'bg-green-500' :
                                job.status === 'failed' ? 'bg-red-500' :
                                'bg-blue-600'
                              }`}
                              style={{
                                width: `${
                                  job.progress_percentage !== null && job.progress_percentage !== undefined
                                    ? job.progress_percentage
                                    : (job.progress_data?.current || 0) / (job.progress_data?.total || 1) * 100
                                }%`,
                              }}
                            />
                          </div>
                          {job.type === 'build_bm25_index' && job.progress_data?.items_processed !== undefined && (
                            <div className="text-xs text-theme-text-secondary mt-1 break-words">
                              {job.progress_data.items_processed.toLocaleString()} / {job.progress_data.total_items?.toLocaleString() || '?'} documents indexed
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  )}

                  {isExpanded && artifactIds.length > 0 && (
                    <div className="min-w-0 max-w-full overflow-x-auto border-t border-theme-border pt-3 mt-1">
                      <JobExpandedView
                        job={job}
                        artifactIds={artifactIds}
                        artifacts={artifactsList}
                      />
                    </div>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {filteredJobs.length === 0 && !isLoading && (
        <Card>
          <div className="text-center py-12">
            <Zap className="h-12 w-12 text-theme-text-secondary mx-auto mb-4" />
            <p className="text-theme-text-secondary">No jobs found</p>
          </div>
        </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Card>
          <div className="flex items-center justify-between">
            <div className="text-sm text-theme-text-secondary">
              Showing {page * pageSize + 1}-{Math.min((page + 1) * pageSize, filteredJobs.length)} of {filteredJobs.length}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <div className="flex gap-1">
                {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                  const pageNum = totalPages <= 10 
                    ? i 
                    : page < 5 
                      ? i 
                      : page > totalPages - 5 
                        ? totalPages - 10 + i 
                        : page - 5 + i;
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`px-3 py-1 rounded ${
                        page === pageNum
                          ? 'bg-primary-600 text-white'
                          : 'bg-theme-surface-hover text-theme-text hover:bg-theme-border-light'
                      }`}
                    >
                      {pageNum + 1}
                    </button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page === totalPages - 1}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Ingest Artifacts Modal */}
      <IngestArtifactsModal
        isOpen={showIngestModal}
        onClose={() => setShowIngestModal(false)}
      />
    </div>
  );
}

export default JobList;
